import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';
import { useRealtimeTags } from 'Core/hooks';

//Components
import {
    SidePanel,
    SidePanelBody,
    SidePanelFooter,
    Button,
    Select,
    Form,
    TimeFrame,
    useTimeFrameComponent
} from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import {
    getDriverPerformanceEventSearchEvents,
    openModalAdvancedFilters,
    reset
} from './redux/slice';

//Helpers
import { getInitialRange } from '../../../../.globals/helpers';
import TagsManage from 'Modules/.common/components/TagsManage';

const AdvancedFilters = ({ formData, applyChanges, hasFilters, loading }) => {
    const dispatch = useDispatch()
    const { messages } = useIntl()

    // Redux States
    const { open, events, loadingEvents, gadgetId } = useSelector((state) => state.driverPerformanceDashboardTabAdvancedFiltersRedux);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);
    const allTags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] })

    // Local States
    const [entitySelected, setEntitySelected] = useState([]);
    const [tempFormData, setTempFormData] = useState(formData);

    //Form refs
    const formRef = Form.useForm();

    const timeFrameOptions = [
        { id: "last7days", label: messages['date_last_7days'] },
        { id: "last15days", label: messages['date_last_15days'] },
        { id: "last30Days", label: messages['date_last_30days'] },
        { id: "custom", label: messages['date_custom'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "last7days")

    // Sync tempFormData when formData changes or modal opens
    useEffect(() => {
        if (open) {
            setTempFormData(formData);

            // Synchronize entitySelected with formData values
            const selectedEntities = [];

            // Add tags to entitySelected
            if (formData?.tags && formData.tags.length > 0) {
                formData.tags.forEach(tag => {
                    selectedEntities.push({
                        id: tag.id,
                        label: tag.label,
                        name: "Tag"
                    });
                });
            }

            // Add drivers to entitySelected
            if (formData?.drivers && formData.drivers.length > 0) {
                formData.drivers.forEach(driver => {
                    selectedEntities.push({
                        id: driver.driverId,
                        label: driver.name,
                        name: "Driver"
                    });
                });
            }

            setEntitySelected(selectedEntities);

            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)

                timeFrame.onChangeSelect(currentTime, formData?.rangeDate?.start, formData?.rangeDate?.end)
            }
        }
    }, [formData, open, gadgetId]);

    useEffect(() => {
        if (open) {
            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);

            // When custom timeframe, cap the range to 30 days
            if (timeFrame?.option?.id === 'custom' && date?.start && date?.end) {
                const start = moment(date.start);
                const end = moment(date.end);
                const diffDays = end.diff(start, 'days');

                if (diffDays > 29) {
                    date.end = start.clone().add(29, 'days').endOf('day').toDate();
                }
            }

            let currentDateRange = {
                start: date?.start,
                end: date?.end
            }

            setTempFormData({
                ...tempFormData,
                rangeDate: currentDateRange,
                timeFrame: timeFrame?.option?.id
            });

        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    useEffect(() => {
        if (open) {
            dispatch(getDriverPerformanceEventSearchEvents({}))
        }
        if (!open) {
            clearFilters()
            setEntitySelected([])
        }
    }, [open])

    // Define initial state for filters
    const INITIAL_FILTER_STATE = {
        rangeDate: {
            start: moment.utc().subtract(7, 'days').startOf('day').toDate(),
            end: moment.utc().subtract(1, 'days').endOf('day').toDate(),
        },
        timeFrame: 'last7days',
        tags: [],
        events: [],
        drivers: [],
        videoEvents: [],
        includeUnassignedDriver: false,
        tagHierarchyRootId: null,
        tagLevel: null
    };

    // Function to clear temporary form data
    const clearFilters = () => {
        setTempFormData(INITIAL_FILTER_STATE);
    }

    // Function to handle Clear All - clears filters in Redux and closes modal
    const handleClearAll = () => {
        const initialFilters = JSON.parse(JSON.stringify(INITIAL_FILTER_STATE));

        if (gadgetId === 'globals') {
            const clearedGlobalFilters = JSON.parse(JSON.stringify(initialFilters));
            applyChanges('globals', clearedGlobalFilters);
        } else if (gadgetId) {
            const clearedGadgetFilters = JSON.parse(JSON.stringify(initialFilters));
            applyChanges(gadgetId, clearedGadgetFilters);
        }

        // Clear entitySelected
        setEntitySelected([]);

        // Close the modal
        dispatch(reset({ open: false }));
    }

    // Function to handle applying filters
    const handleApplyFilters = () => {
        const isCustom = tempFormData?.timeFrame === 'custom';
        const normalizedRange = tempFormData?.rangeDate ? {
            start: isCustom
                ? moment(tempFormData.rangeDate.start).startOf('day').toDate()
                : tempFormData.rangeDate.start,
            end: isCustom
                ? moment(tempFormData.rangeDate.end).endOf('day').toDate()
                : tempFormData.rangeDate.end
        } : tempFormData?.rangeDate;

        const currentTime = {
            ...tempFormData,
            rangeDate: normalizedRange
        };

        dispatch(reset({ open: false }));
        applyChanges(gadgetId || 'globals', currentTime);
    }

    const onChangeEntityTagPicker = useCallback(
        (items, entity) => {
            let newItems = [];

            if (items.length) {
                newItems = items.filter((n) => n.id != -1);
            } else {
                newItems = [];
            }

            // Separate tags and drivers according to the "name" field
            const tags = newItems.filter(item => item.name === "Tag");
            const drivers = newItems.filter(item => item.name === "Driver");

            // Update tempFormData with separated tags and drivers
            setTempFormData({
                ...tempFormData,
                tags: tags.map(tag => ({ id: tag.id, label: tag.label })),
                drivers: drivers.map(driver => ({
                    driverId: driver.id,
                    name: driver.label
                }))
            });

            setEntitySelected(newItems);
        },
        [tempFormData]
    );

    const entities = useMemo(
        () => [
            { id: "Tag", label: messages["driver_performance_events_filters_tags"] },
            { id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] },
        ],
        [messages]
    );

    // Configuration of which filters are visible for each gadget
    const getVisibleFilters = (id) => {
        const filterConfig = {
            EventHighlights: {
                videoEvents: true,
                timeFrame: false,
                events: false,
                drivers: false,
                tags: false,
                unassignedDrivers: true,
                hierarchy: false
            },
            RiskEventsByDriver: {
                videoEvents: false,
                timeFrame: true,
                events: true,
                drivers: true,
                tags: true,
                unassignedDrivers: false,
                hierarchy: false
            },
            RiskEventsByTag: {
                videoEvents: false,
                timeFrame: true,
                events: true,
                drivers: true,
                tags: true,
                unassignedDrivers: false,
                hierarchy: true
            },
            RiskEventDistribution: {
                videoEvents: false,
                timeFrame: true,
                events: true,
                drivers: true,
                tags: true,
                unassignedDrivers: false,
                hierarchy: false
            },
            PendingCoachingCase: {
                videoEvents: false,
                timeFrame: true,
                events: false,
                drivers: true,
                tags: true,
                unassignedDrivers: false,
                hierarchy: false
            },
        };

        return filterConfig[id] || {
            videoEvents: false,
            timeFrame: true,
            events: true,
            drivers: true,
            tags: true,
            unassignedDrivers: false,
            hierarchy: false
        };
    };

    // Get visible filters for current gadget
    const visibleFilters = getVisibleFilters(gadgetId);

    // Hierarchy filter: root tags (parentId null, level 0)
    const hierarchyOptions = useMemo(() => {
        if (!allTags || !allTags.length) return [];
        return allTags
            .filter(tag => !tag.parentId && tag.level === 0 && !tag.disabled)
            .map(tag => ({ id: String(tag.id), label: tag.label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [allTags]);

    // Level options based on the selected hierarchy root
    const levelOptions = useMemo(() => {
        const rootId = tempFormData?.tagHierarchyRootId;
        if (!rootId || !allTags || !allTags.length) return [];

        const numericRootId = String(rootId);
        // Find tags belonging to this hierarchy by checking tagPath contains the rootId
        const hierarchyTags = allTags.filter(tag => {
            if (String(tag.id) === numericRootId) return true;
            if (!tag.tagPath) return false;
            const pathSegments = String(tag.tagPath).split('/');
            return pathSegments.includes(numericRootId);
        });

        // Group tags by level
        const tagsByLevel = {};
        hierarchyTags.forEach(tag => {
            if (tag.level !== undefined && tag.level !== null) {
                if (!tagsByLevel[tag.level]) tagsByLevel[tag.level] = [];
                tagsByLevel[tag.level].push(tag.label);
            }
        });

        // Get distinct levels sorted
        const levels = Object.keys(tagsByLevel)
            .map(Number)
            .sort((a, b) => a - b);

        return levels.map(level => {
            const tagNames = tagsByLevel[level] || [];
            const preview = tagNames.slice(0, 3).join(', ');
            const suffix = tagNames.length > 3 ? '...' : '';
            return {
                id: String(level),
                label: `${messages['risk_events_by_tag_level'] || 'Level'} ${level} (${preview}${suffix})`
            };
        });
    }, [tempFormData?.tagHierarchyRootId, allTags, messages]);

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => {
                if (open) {
                    dispatch(reset({ open: false, gadgetId: null }))
                } else {
                    dispatch(openModalAdvancedFilters({ open: true, gadgetId: 'globals' }))
                }
            }}
            style={{ zIndex: 2, width: '380px', transform: 'translateX(380px)' }}
            classSidePanel={hasFilters() ? "dp-events-tab-advanced-filters-available" : ""}
        >
            <SidePanelBody>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['workOrder_tab_advancedFilters_title']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={handleClearAll}
                            title={messages['risk_events_by_driver_clear_global']}
                        >
                            {messages['workOrder_tab_advancedFilters_clearAll']}
                        </span>
                    </Grid>
                </Grid>

                <div style={{ width: 330, marginTop: 10 }}>
                    <Form
                        ref={formRef.ref}
                        loading={loadingEvents || loading}
                        onValidSubmit={() => handleApplyFilters()}
                        onChangeValues={() => { }}
                        className="dp-events-tab-advanced-filters"
                    >
                        {visibleFilters.videoEvents && (
                            <div style={{ marginBottom: 10 }}>
                                <Select
                                    name="events"
                                    textField="name"
                                    label={messages['driver_performance_events_filters_events']}
                                    valueField="id"
                                    placeholder={messages['driver_performance_events_filters_events']}
                                    isMulti={true}
                                    options={[
                                        { id: 'UnassignedDriver', name: messages['driver_performance_UnassignedDriver'] || 'Unassigned Driver' },
                                        ...(events.length ? events.map((item) => ({
                                            ...item,
                                            name: messages[`driver_performance_${item?.name?.replace(/\s+/g, "")}`] || item.name
                                        })) : [])
                                    ]}
                                    isDisabled={false}
                                    isLoading={loadingEvents}
                                    isClearable={true}
                                    isSearchable={true}
                                    showPillsOutside={true}
                                    value={tempFormData?.videoEvents || []}
                                    onChange={(value) => {
                                        // Allow removing items or adding up to 12 items
                                        if (!value || value.length <= 12) {
                                            setTempFormData({
                                                ...tempFormData,
                                                videoEvents: value
                                            });
                                        }
                                    }}
                                />
                            </div>
                        )}
                        <>
                            {/* Date filter commented out — each gadget now shows its own date range via GadgetDateInfo
                            {
                                visibleFilters.timeFrame && (
                                    <Form.Item
                                        name="timeFrame"
                                        label={messages['driver_performance_events_filters_timeFrame']}
                                        style={{ marginBottom: 10 }}
                                    >
                                        <div style={{ width: 330, marginTop: -10 }} className="dp-events-tab-advanced-filters-timeframe">
                                            <TimeFrame
                                                isRequired
                                                filterSwitch={false}
                                                {...timeFrame}
                                                maxDate={moment()}
                                                dateFormat={dateformat || 'Y/m/d'}
                                                withTime={false}
                                                openDateRangePickerModalAutomatically={false}
                                                startDate={moment(tempFormData?.rangeDate?.start)}
                                                endDate={moment(tempFormData?.rangeDate?.end)}
                                                customRangeDate={30}
                                            />
                                        </div>
                                    </Form.Item>
                                )
                            }
                            */}
                        </>
                        {visibleFilters.events && (
                            <div style={{ marginBottom: 10 }}>
                                <Select
                                    name="events"
                                    textField="name"
                                    label={messages['driver_performance_events_filters_events']}
                                    valueField="id"
                                    placeholder={messages['driver_performance_events_filters_events']}
                                    isMulti={true}
                                    options={[
                                        { id: 'UnassignedDriver', name: messages['driver_performance_UnassignedDriver'] || 'Unassigned Driver' },
                                        ...(events.length ? events.map((item) => ({
                                            ...item,
                                            name: messages[`driver_performance_${item?.name?.replace(/\s+/g, "")}`] || item.name
                                        })) : [])
                                    ]}
                                    isDisabled={false}
                                    isLoading={loadingEvents}
                                    isClearable={true}
                                    isSearchable={true}
                                    showPillsOutside={true}
                                    value={tempFormData?.events || []}
                                    onChange={(value) => {
                                        setTempFormData({
                                            ...tempFormData,
                                            events: value
                                        });
                                    }}
                                />
                            </div>
                        )}
                        {visibleFilters.hierarchy && (
                            <>
                                <div style={{ marginBottom: 10 }}>
                                    <Select
                                        name="tagHierarchyRootId"
                                        textField="label"
                                        label={messages['risk_events_by_tag_hierarchy'] || 'Hierarchy'}
                                        valueField="id"
                                        placeholder={messages['risk_events_by_tag_hierarchy_placeholder'] || 'Select a hierarchy'}
                                        isMulti={false}
                                        options={hierarchyOptions}
                                        isDisabled={false}
                                        isClearable={true}
                                        isSearchable={true}
                                        value={tempFormData?.tagHierarchyRootId
                                            ? hierarchyOptions.find(opt => opt.id === tempFormData.tagHierarchyRootId) || null
                                            : null
                                        }
                                        onChange={(value) => {
                                            setTempFormData({
                                                ...tempFormData,
                                                tagHierarchyRootId: value?.id || null,
                                                tagLevel: null // Reset level when hierarchy changes
                                            });
                                        }}
                                    />
                                </div>
                                {tempFormData?.tagHierarchyRootId && (
                                    <div style={{ marginBottom: 10 }}>
                                        <Select
                                            name="tagLevel"
                                            textField="label"
                                            label={messages['risk_events_by_tag_level'] || 'Level'}
                                            valueField="id"
                                            placeholder={messages['risk_events_by_tag_level_placeholder'] || 'Select a level'}
                                            isMulti={false}
                                            options={levelOptions}
                                            isDisabled={false}
                                            isClearable={true}
                                            isSearchable={false}
                                            value={tempFormData?.tagLevel
                                                ? levelOptions.find(opt => opt.id === tempFormData.tagLevel) || null
                                                : null
                                            }
                                            onChange={(value) => {
                                                setTempFormData({
                                                    ...tempFormData,
                                                    tagLevel: value?.id || null
                                                });
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {(visibleFilters.tags || visibleFilters.drivers) && (
                            <div>
                                <div className="events-tab-tags" style={{ width: '100%', overflow: "auto", marginTop: 5 }}>
                                    <div class="input-gtc-rc-label"><label class="label-gtc-rc">{messages["driver_performance_cases_filters_tags"]}</label></div>
                                    <TagsManage
                                        entities={entities}
                                        onChange={onChangeEntityTagPicker}
                                        hideTagsWithoutEntities={true}
                                        mode="list"
                                        height={400}
                                        value={entitySelected}
                                        noPills={true}
                                        searchByTag={true}
                                        entitySelected={{ id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] }}
                                    />
                                </div>
                            </div>
                        )}
                    </Form>
                </div>
            </SidePanelBody>
            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        isDisabled={loadingEvents || loading}
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loadingEvents || loading}
                        onClick={() => formRef?.submit()}
                        variant="primary"
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default AdvancedFilters
